<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useConfig } from './composables/use-config/useConfig';
import InlineMarkup from './components/inline-markup/InlineMarkupComponent.vue';
import LangSwitcher from './components/lang-switcher/LangSwitcherComponent.vue';
import MainNav from './components/nav/MainNavComponent.vue';
import MobileNav from './components/nav/MobileNavComponent.vue';
import { useIFrameMode } from './composables/useIFrameMode';

const appName = 'Sicherheitstacho';
const { currentRoute } = useRouter();
document.querySelector('body')?.setAttribute('data-mode', 'dark');
document.querySelector('body')?.classList.add('custom-scroll', 'wide');
const { config } = useConfig();
const { isIFrameMode } = useIFrameMode();
if (isIFrameMode.value) {
  document.getElementById('favicon')?.setAttribute('href', '/favicon-blank.svg');
}
</script>

<template>
  <scale-telekom-app-shell>
    <scale-telekom-header
      :class="{ hidden: isIFrameMode }"
      :app-name="appName"
      ref="header"
      Type="slim"
      ><!-- don't ask, but the the upper case T in the "Type" attribute above is not a typo, it gets removed if written with a lower case t  -->
      <main-nav></main-nav>
      <div slot="functions">
        <scale-telekom-nav-list
          variant="functions"
          alignment="right"
          aria-label="Functions Menu"
        >
          <scale-telekom-nav-item
            hide-on-desktop
            aria-label="Menu"
          >
            <button>
              <scale-icon-action-menu></scale-icon-action-menu>
            </button>
            <scale-telekom-nav-flyout variant="mobile">
              <scale-telekom-mobile-flyout-canvas :app-name="appName">
                <mobile-nav></mobile-nav>
                <lang-switcher
                  slotName="mobile-before-main-nav"
                  aligment="left"
                ></lang-switcher>
              </scale-telekom-mobile-flyout-canvas>
            </scale-telekom-nav-flyout>
          </scale-telekom-nav-item>
        </scale-telekom-nav-list>
        <lang-switcher
          slotName="functions"
          alignment="right"
          :hide-on-mobile="true"
        ></lang-switcher>
      </div>
    </scale-telekom-header>
    <main>
      <div class="container font-mono">
        <router-view></router-view>
      </div>
    </main>

    <scale-telekom-footer
      type="minimal"
      slot="footer"
      :class="{ hidden: isIFrameMode }"
    >
      <scale-telekom-footer-content>
        <div
          slot="notice"
          class="notice"
        >
          <div>© Deutsche Telekom Security GmbH</div>
          <div class="app-info">
            <inline-markup :content="config.infoText" />
          </div>
          <div class="notice-link">
            <a
              href="/NOTICE.txt"
              target="_blank"
              rel="noopener noreferrer"
              >{{ $t('notice') }}</a
            >
          </div>
        </div>

        <ul slot="navigation">
          <li>
            <router-link
              :to="{ name: 'imprint', params: { lang: currentRoute.params['lang'] || 'en' } }"
              >{{ $t('imprint') }}</router-link
            >
          </li>
          <li>
            <router-link
              :to="{ name: 'privacy', params: { lang: currentRoute.params['lang'] || 'en' } }"
              >{{ $t('privacy') }}</router-link
            >
          </li>
          <li>
            <router-link
              :to="{ name: 'disclaimer', params: { lang: currentRoute.params['lang'] || 'en' } }"
              >{{ $t('disclaimer') }}</router-link
            >
          </li>
        </ul>
      </scale-telekom-footer-content>
    </scale-telekom-footer>
  </scale-telekom-app-shell>
</template>

<style scoped lang="scss">
.hidden {
  display: none;
}
.notice {
  display: flex;
  width: 100%;
  text-align: center;
}
.app-info {
  flex-grow: 1;
}
scale-telekom-header {
  position: relative;
  z-index: 1001;
}

@media (max-width: 639.98px) {
  scale-telekom-header::part(body) {
    margin-left: 74px;
  }

  scale-telekom-header::part(bottom-app-name) {
    display: block;
    margin-right: var(--telekom-spacing-composition-space-06);
  }
}

scale-telekom-app-shell {
  --scl-grid-max-width: 1604px;
  @media (min-width: 3000px) {
    --scl-grid-max-width: calc(100vw * 0.6);
  }
}

scale-telekom-app-shell::part(content) {
  background-color: var(--telekom-color-background-surface);
}
scale-telekom-footer {
  --background-footer-minimal: var(--telekom-color-background-surface);
}

.container {
  max-width: 1604px;
  @media (min-width: 3000px) {
    max-width: calc(100vw * 0.6);
  }
  margin-left: auto;
  margin-right: auto;
  padding-left: var(--telekom-spacing-unit-x6);
  padding-right: var(--telekom-spacing-unit-x6);
}
</style>
